import React from "react";
import { LucideIcon, CheckCircle2, Loader2, Circle, AlertTriangle } from "lucide-react";
import { cn } from "../lib/utils";

export type TimelineStatus = "pending" | "active" | "completed" | "failed";

export interface TimelineItem {
  id: string;
  title: string;
  description?: string;
  status?: TimelineStatus;
  icon?: LucideIcon;
  meta?: string;
}

interface StatusTimelineProps {
  items: TimelineItem[];
  className?: string;
  dense?: boolean;
}

const STATUS_CONFIG: Record<
  TimelineStatus,
  {
    label: string;
    icon: LucideIcon;
    iconClass: string;
    circleClass: string;
    labelClass: string;
  }
> = {
  pending: {
    label: "Pending",
    icon: Circle,
    iconClass: "text-muted-foreground",
    circleClass: "border-muted-foreground/40 bg-transparent",
    labelClass: "text-muted-foreground",
  },
  active: {
    label: "In Progress",
    icon: Loader2,
    iconClass: "text-blue-500 animate-spin",
    circleClass: "border-blue-500/40 bg-blue-500/10",
    labelClass: "text-blue-600 dark:text-blue-400",
  },
  completed: {
    label: "Completed",
    icon: CheckCircle2,
    iconClass: "text-emerald-500",
    circleClass: "border-emerald-500/40 bg-emerald-500/10",
    labelClass: "text-emerald-600 dark:text-emerald-400",
  },
  failed: {
    label: "Failed",
    icon: AlertTriangle,
    iconClass: "text-red-500",
    circleClass: "border-red-500/40 bg-red-500/10",
    labelClass: "text-red-600 dark:text-red-400",
  },
};

export const StatusTimeline: React.FC<StatusTimelineProps> = ({
  items,
  className,
  dense = false,
}) => {
  return (
    <div className={cn("space-y-4", className)}>
      {items.map((item, index) => {
        const status = item.status ?? "pending";
        const config = STATUS_CONFIG[status];
        const Icon = item.icon ?? config.icon;

        return (
          <div key={item.id ?? index} className="flex gap-3">
            <div
              className={cn(
                "flex h-9 w-9 shrink-0 items-center justify-center rounded-full border-2 transition-colors",
                config.circleClass
              )}
            >
              <Icon className={cn("h-4 w-4", config.iconClass)} />
            </div>
            <div className="flex-1 space-y-1">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="space-y-0.5">
                  <p className="text-sm font-medium leading-none">{item.title}</p>
                  {item.description && (
                    <p className={cn("text-xs", dense ? "" : "text-muted-foreground")}>
                      {item.description}
                    </p>
                  )}
                  {item.meta && (
                    <p className="text-xs text-muted-foreground">{item.meta}</p>
                  )}
                </div>
                <span
                  className={cn(
                    "text-xs font-medium uppercase tracking-wide",
                    config.labelClass
                  )}
                >
                  {config.label}
                </span>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default StatusTimeline;
